import { Formik } from "formik";
import omit from "lodash/omit";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createContract } from "../../../../../../../../actions/employers/contractActions";
import { getTempPrerequisites } from "../../../../../../../../actions/employers/tempPrerequisiteActions";
import { setProfileDrawerVisible } from "../../../../../../../../actions/shared/uiActions";
import { getJobCast } from "../../../../../../../../selectors/jobcastSelectors";
import {
  buildFullName,
  mapKeysToSnakeCase,
} from "../../../../../../../../util/formatHelpers";
import { string, object, array, ref } from "../../../../../../../../util/yup";
import useSnackbar from "../../../../../../../General/customHooks/useSnackbar";
import { mergePrerequisites } from "../../../../../helpers";
import { getCurrentOrganization } from "../../../../../../../../selectors/organizationSelectors";
import { getCurrentIndividual } from "../../../../../../../../selectors/individualSelectors";

function FormInitializer({ candidate, children }) {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const currentOrganization = useSelector(getCurrentOrganization);
  const currentIndividual = useSelector(getCurrentIndividual);
  const organizationPrerequisites = useSelector(getTempPrerequisites);
  const jobCast = useSelector((state) =>
    getJobCast(state, candidate.attributes.jobcastId)
  );
  const jobcastPrerequisites = jobCast.attributes.tempPrerequisites;

  const costCenters = useSelector((state) => state.costCenters.items);

  const initialValues = useMemo(() => {
    const {
      agencyName,
      agencyLogoUrl,
      recruiterFirstName,
      recruiterLastName,
      hourlyBillRate,
      availableStartDate,
      defaultTimesheetSubmissionParty,
    } = candidate.attributes;

    const costCenterIds = Object.keys(costCenters);
    return {
      // AGENCY INFORMATION (uneditable fields)
      agencyName,
      agencyLogoUrl,
      recruiterName: buildFullName(recruiterFirstName, recruiterLastName),
      hourlyBillRate,
      // WORK INFORMATION
      scheduledStartDate: availableStartDate,
      scheduledEndDate: null,
      primaryWorkState: null,
      primaryWorkZipCode: null,
      timesheetSubmissionParty:
        defaultTimesheetSubmissionParty ||
        currentOrganization.defaultTimesheetSubmissionParty,
      statementOfWorkDescription: jobCast.attributes.description.replace(
        /<img[^>]*>/g,
        ""
      ),
      temp_prerequisites: mergePrerequisites(
        jobcastPrerequisites,
        organizationPrerequisites
      ),
      sosSignerIndividualId: currentIndividual.id,
      hrpbSignerIndividualId: "",
      // BILLING INFORMATION
      costCenterId: jobCast.attributes.costCenterId || (costCenterIds.length === 1 ? costCenterIds[0] : null),
      invoiceMemo: null,
      allocations: [{ code: "", type: "gl_string" }],
      contractClientIndividualIds:
        jobCast.attributes.contractClientIndividualIds || [],
    };
  }, [
    candidate.attributes,
    costCenters,
    jobcastPrerequisites,
    organizationPrerequisites,
    jobCast.attributes.costCenterId,
    jobCast.attributes.contractClientIndividualIds,
    jobCast.attributes.description,
  ]);

  const validationSchema = useMemo(
    () => {
      const requireHrpbSigner = currentOrganization.hrpbSignerRequired;
      const requireGlString = currentOrganization.requirePoNumber;

      return object().shape({
        scheduledStartDate: string().nullable().required("Cannot be blank"),
        scheduledEndDate: string().nullable(),
        primaryWorkState: string().nullable().required("Cannot be blank"),
        primaryWorkZipCode: string()
          .nullable()
          .required("Cannot be blank")
          .matches(/^\d*$/, "Digits only")
          .min(5, "Must be 5 characters")
          .max(5, "Must be 5 characters"),
        timesheetSubmissionParty: string()
          .nullable()
          .required("Cannot be blank")
          .oneOf(["client", "supplier"]),
        statementOfWorkDescription: string()
          .nullable()
          .minWordsWithoutHTML(3, "Must be at least 3 words")
          .required("Cannot be blank"),
        costCenterId: string().nullable().required("Cannot be blank"),
        invoiceMemo: string()
          .nullable()
          .max(600, "Must be less than 600 characters"),
        allocations: requireGlString
          ? array().of(
              object().shape({
                type: string().required("Cannot be blank").oneOf(["project", "gl_string"]),
                code: string().required("Cannot be blank")
                  .when("type", {
                    is: "gl_string",
                    then: string().required("Cannot be blank")
                      .test("gl-format", "Must be 7 sections separated by '.' (ex: 004.0000.0000.150000.A000.000.0000)", (val) => /^[^.]+(\.[^.]+){6}$/.test(val)),
                  }),
              })
            ).min(1, "Must have at least one allocation")
          : array().of(object()),
        contractClientIndividualIds: array()
          .of(string())
          .min(1, "Cannot be blank"),
        hrpbSignerIndividualId: requireHrpbSigner
          ? string().nullable().required("Cannot be blank")
            .notOneOf([ref("sosSignerIndividualId")], "Must be different from Statement of Services Signatory")
          : string().nullable(),
      });
    },
    [currentOrganization]
  );

  const onSubmit = useCallback(
    (values, { setSubmitting }) => {
      const sosSignerIndividualIds = [
        { id: values.sosSignerIndividualId, primary: true },
        values.hrpbSignerIndividualId ? { id: values.hrpbSignerIndividualId, primary: false } : null,
      ].filter(Boolean);

      const submitValues = {
        ...mapKeysToSnakeCase(
          omit(values, [
            "agencyName",
            "agencyLogoUrl",
            "recruiterName",
            "hourlyBillRate",
            "sosSignerIndividualId",
            "hrpbSignerIndividualId",
          ])
        ),
        sos_signer_individual_ids: sosSignerIndividualIds,
        temp_prerequisites: values.temp_prerequisites.filter(
          (prerequisite) => prerequisite.checked
        ),
        recruiter_submission_id: candidate.id,
      };

      return dispatch(createContract(submitValues))
        .then(() => {
          setSubmitting(false);
          dispatch(setProfileDrawerVisible(false));
          snackbar.showMessage(
            "Candidate successfully advanced",
            null,
            () => {},
            { duration: 3000 }
          );
          // open rate modal
        })
        .catch((errorData) => {
          setSubmitting(false);
          if (errorData?.error) {
            snackbar.showMessage(
              errorData.error,
              null,
              () => {},
              { duration: 5000 }
            );
          }
        });
    },
    [dispatch, candidate.id, snackbar]
  );

  return (
    <Formik {...{ initialValues, validationSchema, onSubmit }}>
      {children}
    </Formik>
  );
}

export default FormInitializer;
